// Competition context provider
import React, { createContext, useContext, useState, useEffect } from 'react';
import { useAuth } from './AuthContext';
import {
  getCompetition,
  getPlayersForCompetition,
  getCategoriesForCompetition,
  getGamesForCompetition,
  getResultsForCompetition
} from '../services';
import { getPlayerToken, createPlayerToken } from '../services/tokenService';
import { getTokenForCompetition, storeToken } from '../utils/localStorage';

const CompetitionContext = createContext();

export const useCompetition = () => {
  const context = useContext(CompetitionContext);
  if (!context) {
    throw new Error('useCompetition must be used within a CompetitionProvider');
  }
  return context;
};

// Safe version that returns null when not in a competition context
// Use this for components that may be used outside of a competition (e.g., HamburgerMenu)
export const useCompetitionSafe = () => {
  return useContext(CompetitionContext);
};

export const CompetitionProvider = ({ competitionId, children }) => {
  const { authUid } = useAuth();
  const [competition, setCompetition] = useState(null);
  const [currentPlayer, setCurrentPlayer] = useState(null);
  const [players, setPlayers] = useState([]);
  const [categories, setCategories] = useState([]);
  const [games, setGames] = useState([]);
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Check if current player is the organizer
  // Primary check: player ID matches organizerId
  // Fallback: find the organizer player and check if current user's authUid matches their odplayerId
  const isOrganizer = (() => {
    if (!currentPlayer || !competition) return false;
    // Primary check
    if (currentPlayer.id === competition.organizerId) return true;
    // Fallback: check if this user owns the organizer player via odplayerId
    const organizerPlayer = players.find(p => p.id === competition.organizerId);
    if (organizerPlayer?.odplayerId && organizerPlayer.odplayerId === authUid) return true;
    // Also check if current player has odplayerId matching the organizer's odplayerId
    if (currentPlayer.odplayerId && organizerPlayer?.odplayerId === currentPlayer.odplayerId) return true;
    return false;
  })();

  const loadCompetitionData = async () => {
    try {
      setLoading(true);
      setError(null);

      // Load competition
      const comp = await getCompetition(competitionId);
      if (!comp) {
        setError('Competition not found');
        setLoading(false);
        return;
      }
      setCompetition(comp);

      // Load all competition data in parallel first
      const [playersData, categoriesData, gamesData, resultsData] = await Promise.all([
        getPlayersForCompetition(competitionId),
        getCategoriesForCompetition(competitionId),
        getGamesForCompetition(competitionId),
        getResultsForCompetition(competitionId)
      ]);

      setPlayers(playersData);
      setCategories(categoriesData);
      setGames(gamesData);
      setResults(resultsData);

      // Verify player access via token
      let tokenId = getTokenForCompetition(competitionId);
      let token = tokenId ? await getPlayerToken(tokenId) : null;
      let player = null;

      // If no valid token, check if user has a player in this competition via odplayerId
      if (!token || token.authUid !== authUid) {
        // Look for a player with matching odplayerId (for league members with accounts)
        const playerByOdplayerId = playersData.find(p => p.odplayerId === authUid);

        if (playerByOdplayerId) {
          // Auto-create a token for this player
          console.log('Auto-creating token for player with matching odplayerId:', playerByOdplayerId.displayName);
          const newTokenId = await createPlayerToken(competitionId, playerByOdplayerId.id, authUid);
          storeToken(competitionId, newTokenId);
          token = { playerId: playerByOdplayerId.id, authUid };
          player = playerByOdplayerId;
        } else {
          setError('No access token found');
          setLoading(false);
          return;
        }
      } else {
        // Find current player from token
        player = playersData.find(p => p.id === token.playerId);
      }

      setCurrentPlayer(player);

      // Debug logging
      console.log('=== Competition Debug ===');
      console.log('Competition organizerId:', comp.organizerId);
      console.log('Token playerId:', token?.playerId);
      console.log('Current player id:', player?.id);
      console.log('Current player odplayerId:', player?.odplayerId);
      console.log('AuthUid:', authUid);
      console.log('Players:', playersData.map(p => ({ id: p.id, displayName: p.displayName, odplayerId: p.odplayerId })));
      console.log('Primary check (player.id === organizerId):', player?.id === comp.organizerId);
      const organizerPlayer = playersData.find(p => p.id === comp.organizerId);
      console.log('Organizer player:', organizerPlayer ? { id: organizerPlayer.id, displayName: organizerPlayer.displayName, odplayerId: organizerPlayer.odplayerId } : 'NOT FOUND');

      setLoading(false);
    } catch (err) {
      console.error('Error loading competition data:', err);
      setError(err.message);
      setLoading(false);
    }
  };

  useEffect(() => {
    if (competitionId && authUid) {
      loadCompetitionData();
    }
  }, [competitionId, authUid]);

  const refreshCompetition = async () => {
    await loadCompetitionData();
  };

  const value = {
    competition,
    currentPlayer,
    players,
    categories,
    games,
    results,
    loading,
    error,
    isOrganizer,
    refreshCompetition
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading competition...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <p className="text-red-600 font-semibold">Error: {error}</p>
          <button
            onClick={refreshCompetition}
            className="mt-4 px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <CompetitionContext.Provider value={value}>
      {children}
    </CompetitionContext.Provider>
  );
};
